/** Small shared pieces built on top of the design system. */
import React from 'react'
import { Badge, Button, Card, EmptyState } from './ds'
import { Ic } from './icons'
import { STATUS_BADGE, STATUS_LABEL, type QueueStatus } from '@shared/types'

/** Renders a queue status using the design system's status token colours. */
export function StatusBadge({ status, dot = true }: { status: QueueStatus; dot?: boolean }) {
  return (
    <Badge status={STATUS_BADGE[status]} dot={dot}>
      {STATUS_LABEL[status]}
    </Badge>
  )
}

export function ProgressBar({
  value,
  max,
  warnAt = 0.9,
}: {
  value: number
  max: number
  /** fraction above which the bar turns amber */
  warnAt?: number
}) {
  const fraction = max > 0 ? Math.min(1, value / max) : 0
  const pct = Math.round(fraction * 100)
  const colour =
    fraction >= 1
      ? 'var(--status-failed-dot)'
      : fraction >= warnAt
        ? 'var(--status-pending-dot)'
        : 'var(--accent)'

  return (
    <div
      style={{
        height: 5,
        borderRadius: 999,
        background: 'var(--surface-sunken)',
        overflow: 'hidden',
      }}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: colour }} />
    </div>
  )
}

/** Progress bar plus "212/480", as used in the campaigns table. */
export function ProgressCell({ sent, total }: { sent: number; total: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 140 }}>
      <div style={{ flex: 1 }}>
        <ProgressBar value={sent} max={total} warnAt={2} />
      </div>
      <span
        style={{
          fontSize: 12,
          color: 'var(--text-muted)',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {sent}/{total}
      </span>
    </div>
  )
}

export function Spinner({ size = 18 }: { size?: number }) {
  return <Ic.spinner size={size} className="animate-spin" />
}

/**
 * One place to handle the loading / error / empty triad, so every screen fails
 * the same way instead of rendering a blank panel.
 */
export function QueryState({
  loading,
  error,
  empty,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  onRetry,
  children,
}: {
  loading: boolean
  error: string | null
  empty?: boolean
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: React.ReactNode
  onRetry?: () => void
  children: React.ReactNode
}) {
  if (loading) {
    return (
      <Card>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '24px 0',
            color: 'var(--text-muted)',
            fontSize: 13,
          }}
        >
          <Spinner />
          Loading…
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <EmptyState
          icon={<Ic.warning size={18} />}
          title="Couldn't load this"
          description={error}
          action={
            onRetry ? (
              <Button variant="secondary" size="sm" icon={<Ic.retry size={14} />} onClick={onRetry}>
                Try again
              </Button>
            ) : undefined
          }
        />
      </Card>
    )
  }

  if (empty) {
    return (
      <Card>
        <EmptyState
          icon={<Ic.inbox size={18} />}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      </Card>
    )
  }

  return <>{children}</>
}

// --- the dense list "box" pattern (Dashboard, Queue, Campaigns, Leads, …) -----

/**
 * ONE bordered container per list — rows live inside, never floating cards.
 *
 * `dense` is the readability exception to the glass theme: a list of 100 rows
 * gets an opaque fill (.lm-solid) so 12px text never sits over a gradient blob.
 * Everything else stays frosted.
 */
export function Box({
  children,
  style,
  dense,
  className,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
  dense?: boolean
  className?: string
}) {
  return (
    <div
      className={[dense ? 'lm-solid' : 'lm-glass', className].filter(Boolean).join(' ')}
      style={{
        background: 'var(--surface-box)',
        border: '1px solid var(--glass-edge)',
        borderRadius: 'var(--glass-radius)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** Header strip — holds a BoxLabel title or grid column labels + an action. */
export function BoxHeader({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        borderBottom: '1px solid var(--border-default)',
        background: 'var(--surface-box-header)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** Uppercase column/title label inside a BoxHeader. */
export function BoxLabel({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--text-faint)',
        textTransform: 'uppercase',
        letterSpacing: '.05em',
        ...style,
      }}
    >
      {children}
    </span>
  )
}

/** Plain accent text-link for the right side of a BoxHeader ("Manage all"). */
export function BoxAction({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="tap"
      onClick={onClick}
      style={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontSize: 12.5,
        color: 'var(--accent-text)',
      }}
    >
      {children}
    </button>
  )
}

/** Packed row: 8px 12px, hairline divider, header-tinted hover. */
export function BoxRow({
  children,
  onClick,
  last,
  style,
}: {
  children: React.ReactNode
  onClick?: () => void
  last?: boolean
  style?: React.CSSProperties
}) {
  const [hover, setHover] = React.useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        borderBottom: last ? 'none' : '1px solid var(--border-row)',
        background: hover ? 'var(--surface-box-header)' : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background var(--dur-fast) var(--ease-out)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** Tiny status pill (10.5px) — the box-pattern companion to the DS Badge. */
export function Pill({
  bg,
  fg,
  weight = 600,
  children,
}: {
  bg: string
  fg: string
  weight?: number
  children: React.ReactNode
}) {
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: weight,
        borderRadius: 999,
        padding: '1px 7px',
        background: bg,
        color: fg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  )
}

/**
 * Six-colour avatar palette. The colour is chosen by hashing the name, so a
 * given lead always gets the same one across reloads and pages.
 */
const AVATAR_COLORS = [
  '#5b5bd6', // indigo
  '#137540', // green
  '#b45309', // orange
  '#be185d', // pink
  '#274bcf', // blue
  '#946210', // gold
]

/**
 * FNV-1a plus a final avalanche mix. A plain djb2 (`*33`) distributes badly
 * across 6 buckets — 33 ≡ 3 (mod 6) — and clustered most names onto one colour.
 * All-integer maths keeps the result identical across reloads and machines.
 */
export function avatarColor(seed: string): string {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  h ^= h >>> 15
  h = Math.imul(h, 2246822507)
  h ^= h >>> 13
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

/** 20px round initial, colour-keyed to the name. */
export function Avatar({ name, size = 20 }: { name: string; size?: number }) {
  const letter = (name.trim()[0] ?? '?').toUpperCase()
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: '50%',
        background: avatarColor(name || '?'),
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.52,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {letter}
    </span>
  )
}

/** Label + hint pair used in the settings-style rows. */
export function RowLabel({ title, hint }: { title: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{title}</div>
      {hint && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>{hint}</div>
      )}
    </div>
  )
}

export const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  justifyContent: 'space-between',
}

/**
 * Monospaced inline value — ONLY for genuinely technical raw strings (SMTP
 * responses, config URLs, message ids). Emails, hosts, and usernames are
 * normal content: use Val.
 */
export function Mono({
  children,
  muted,
  size = 12,
}: {
  children: React.ReactNode
  muted?: boolean
  size?: number
}) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: size,
        color: muted ? 'var(--text-muted)' : 'var(--text-secondary)',
      }}
    >
      {children}
    </span>
  )
}

/** Secondary inline value — emails, hosts, usernames. Sans, muted. */
export function Val({
  children,
  muted = true,
  size = 13,
}: {
  children: React.ReactNode
  muted?: boolean
  size?: number
}) {
  return (
    <span style={{ fontSize: size, color: muted ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
      {children}
    </span>
  )
}

// --- rows per page -------------------------------------------------------------

/** The sizes every paginated table offers. */
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 250]

/**
 * Remembered rows-per-page for one table. The key scopes the memory
 * ('leads', 'queue', 'logs'), so each table keeps its own choice across
 * visits; the fallback preserves each table's historical default for anyone
 * who never touches the control.
 */
export function usePageSize(table: string, fallback: number): [number, (n: number) => void] {
  const storageKey = `lm-pagesize-${table}`
  const [size, setSize] = React.useState<number>(() => {
    const saved = Number(localStorage.getItem(storageKey))
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : fallback
  })
  const update = React.useCallback(
    (n: number) => {
      localStorage.setItem(storageKey, String(n))
      setSize(n)
    },
    [storageKey],
  )
  return [size, update]
}

/**
 * The ONE rows-per-page dropdown every paginated footer shares — Leads, Queue
 * and Send logs all render this, so the tables cannot drift apart.
 */
export function PageSizeSelect({
  value,
  onChange,
}: {
  value: number
  onChange: (n: number) => void
}) {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: 'var(--text-muted)',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
    >
      Rows
      <select
        aria-label="Rows per page"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          height: 24,
          padding: '0 6px',
          borderRadius: 6,
          border: '1px solid var(--border-default)',
          background: 'var(--surface-card)',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          cursor: 'pointer',
        }}
      >
        {PAGE_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </label>
  )
}
